import { getStore as getMailListLayoutStore, type MailListColumnHeaderType, getColumnMinWidth } from "owa-mail-layout";
import type { ListViewColumnHeadersOptions } from "owa-outlook-service-option-store";
import { getDefaultOptions } from "owa-outlook-service-options";
import { getOptionsForFeature, OwsOptionsFeatureType } from "owa-outlook-service-option-store";
import { setItem } from "owa-local-storage";

/**
 * In-memory cache of sorted content column widths keyed by sort column type.
 */
const sortedContentColumnWidthCache = new Map<MailListColumnHeaderType, number>();

/** Tracks the sort column type from the last calculateColumnWidths call so we
 *  can snapshot any manual-resize changes from the store into the cache before
 *  the context switches to a different (or no) sort type. */
let lastActiveSortColumnType: MailListColumnHeaderType | null = null;

export default function calculateColumnWidths(isInitialLayout: boolean, containerWidth: number, prevContainerWidth: React.MutableRefObject<number | null>, sortColumnHeaderType: MailListColumnHeaderType | null): {
    senderColumnWidth: number | null;
    subjectColumnWidth: number | null;
    receivedColumnWidth: number | null;
    sortedContentColumnWidth: number | null;
} {
    // Before switching to a different sort type, snapshot the store's current
    // sorted column width into the cache so it can be restored if the user
    // navigates back to a folder with this sort type. Only do this on an
    // actual sort-type change — not on every call — to avoid feeding
    // auto-resize output back into the cache (which causes a compounding
    // feedback loop where the sorted column grows disproportionately).
    if (lastActiveSortColumnType !== null && lastActiveSortColumnType !== sortColumnHeaderType) {
        const currentStoreSortedWidth = getMailListLayoutStore().sortedContentColumnWidth;
        if (currentStoreSortedWidth && currentStoreSortedWidth > 0) {
            sortedContentColumnWidthCache.set(lastActiveSortColumnType, currentStoreSortedWidth);
        }
    }
    lastActiveSortColumnType = sortColumnHeaderType;
    
    /**
     * We should only do this resize work if the width of the container
     * has changed (or it's the initial layout).
     */
    if (!isInitialLayout && prevContainerWidth?.current === containerWidth) {
        return getCurrentColumnWidths(sortColumnHeaderType);
    }
    prevContainerWidth.current = containerWidth;
    
    const preferredColumnWidths = getPreferredColumnWidths(sortColumnHeaderType);
    
    const senderColumnMinWidth = getColumnMinWidth(0);
    const subjectColumnMinWidth = getColumnMinWidth(1);
    const receivedColumnMinWidth = getColumnMinWidth(2);
    const sortedContentColumnMinWidth = sortColumnHeaderType
        ? getColumnMinWidth(sortColumnHeaderType)
        : 0;
    
    // We need to make sure the columns are at least as wide as their min width.
    const adjustedSenderColumnWidth = Math.max(preferredColumnWidths.senderColumnWidth, senderColumnMinWidth);
    const adjustedSubjectColumnWidth = Math.max(preferredColumnWidths.subjectColumnWidth, subjectColumnMinWidth);
    const adjustedReceivedColumnWidth = Math.max(preferredColumnWidths.receivedColumnWidth, receivedColumnMinWidth);
    
    const adjustedSortedColumnWidth = Math.max(preferredColumnWidths.sortedContentColumnWidth, sortedContentColumnMinWidth);
    
    /**
     * Calculates the remaining space in the container after the columns,
     * resize handles, and extra space is accounted for.
     */
    const resizeHandleWidth = 2;
    
    const remainingSpace = containerWidth -
        adjustedSenderColumnWidth -
        adjustedSubjectColumnWidth -
        adjustedReceivedColumnWidth -
        adjustedSortedColumnWidth -
        resizeHandleWidth;
    
    /** Keep the ratio between the sender, subject, and sorted column widths the same
     * as the user increases and decreases the width of the entire window.
     *
     * Use the stable min width for the sorted column in proportion calculation
     * instead of the adjusted (cached) value. Sender/subject preferred widths
     * come from persisted OWS options and are stable, but the sorted column's
     * preferred width comes from an in-memory cache that is updated by prior
     * calculations.
     */
    const totalWidth = adjustedSenderColumnWidth +
        adjustedSubjectColumnWidth +
        (sortColumnHeaderType ? sortedContentColumnMinWidth : 0);
    const senderProportion = totalWidth > 0 ? adjustedSenderColumnWidth / totalWidth : 0;
    const subjectProportion = totalWidth > 0 ? adjustedSubjectColumnWidth / totalWidth : 0;
    const sortedProportion = totalWidth > 0 && sortColumnHeaderType ? sortedContentColumnMinWidth / totalWidth : 0;
    
    let newSenderColumnWidth = adjustedSenderColumnWidth;
    let newSubjectColumnWidth = adjustedSubjectColumnWidth;
    let newReceivedColumnWidth = adjustedReceivedColumnWidth;
    let newSortedColumnWidth = adjustedSortedColumnWidth;
    
    /**
     * If the value is positive, that means there's extra space we can
     * use to increase the column sizes. We allocate space in a way that
     * maintains the ratio between the sender, subject, and sorted columns.
     */
    if (remainingSpace > 0) {
        const spaceToAllocate = Math.floor(remainingSpace);
        newSenderColumnWidth = newSenderColumnWidth + spaceToAllocate * senderProportion;
        newSubjectColumnWidth = newSubjectColumnWidth + spaceToAllocate * subjectProportion;
        
        if (sortColumnHeaderType !== null) {
            newSortedColumnWidth = newSortedColumnWidth + spaceToAllocate * sortedProportion;
        }
        
        // Received Column Header extends to end of container. Adjust its width so that it fills remaining space.
        newReceivedColumnWidth =
            containerWidth - newSenderColumnWidth - newSubjectColumnWidth - newSortedColumnWidth;
    }
    
    /**
     * If the value is negative, that means we need to reduce the column
     * sizes so they can fit.
     */
    if (remainingSpace < 0) {
        let spaceToTrim = Math.floor(Math.abs(remainingSpace));
        
        // First, trim the received column as much as possible without going below its min width
        const receivedColumnTrimSpace = Math.max(0, newReceivedColumnWidth - receivedColumnMinWidth);
        const receivedColumnTrimAmount = Math.min(spaceToTrim, receivedColumnTrimSpace);
        newReceivedColumnWidth = newReceivedColumnWidth - receivedColumnTrimAmount;
        spaceToTrim = spaceToTrim - receivedColumnTrimAmount;
        
        // Apply remaining space reduction to sender, subject, and sorted columns.
        // We trim each column down toward its minimum, distributing proportionally
        // and cascading any remainder that a column can't absorb to the others.
        if (spaceToTrim > 0) {
            // Trim subject column first (typically the largest and has most room)
            const subjectTrimSpace = Math.max(0, newSubjectColumnWidth - subjectColumnMinWidth);
            const subjectTrimAmount = Math.min(spaceToTrim, subjectTrimSpace);
            newSubjectColumnWidth = newSubjectColumnWidth - subjectTrimAmount;
            spaceToTrim = spaceToTrim - subjectTrimAmount;
        }
        
        if (spaceToTrim > 0) {
            // Trim sender column
            const senderTrimSpace = Math.max(0, newSenderColumnWidth - senderColumnMinWidth);
            const senderTrimAmount = Math.min(spaceToTrim, senderTrimSpace);
            newSenderColumnWidth = newSenderColumnWidth - senderTrimAmount;
            spaceToTrim = spaceToTrim - senderTrimAmount;
        }
        
        if (spaceToTrim > 0 && sortColumnHeaderType) {
            // Trim sorted column
            const sortedTrimSpace = Math.max(0, newSortedColumnWidth - sortedContentColumnMinWidth);
            const sortedTrimAmount = Math.min(spaceToTrim, sortedTrimSpace);
            newSortedColumnWidth = newSortedColumnWidth - sortedTrimAmount;
            spaceToTrim = spaceToTrim - sortedTrimAmount;
        }
        
        // Ensure received column fills exactly the remaining space so columns never exceed containerWidth.
        newReceivedColumnWidth = Math.max(receivedColumnMinWidth, containerWidth - newSenderColumnWidth - newSubjectColumnWidth - newSortedColumnWidth);
    }
    
    updateLocalColumnWidths(newSenderColumnWidth, newSubjectColumnWidth, newReceivedColumnWidth, newSortedColumnWidth);
    
    return {
        senderColumnWidth: newSenderColumnWidth,
        subjectColumnWidth: newSubjectColumnWidth,
        receivedColumnWidth: newReceivedColumnWidth,
        sortedContentColumnWidth: newSortedColumnWidth,
    };
}

const getPreferredColumnWidths = (sortColumnHeaderType: MailListColumnHeaderType | null) => {
    /**
     * Fetch the user preferences from the server. If no preferences are found,
     * fall back to the default values for these settings.
     */
    const listViewColumnHeadersOptions = getOptionsForFeature<ListViewColumnHeadersOptions>(OwsOptionsFeatureType.ListViewColumnHeaders) ||
        (getDefaultOptions()[OwsOptionsFeatureType.ListViewColumnHeaders] as ListViewColumnHeadersOptions);
    
    return {
        senderColumnWidth: listViewColumnHeadersOptions.senderColumnWidth,
        subjectColumnWidth: listViewColumnHeadersOptions.subjectColumnWidth,
        receivedColumnWidth: listViewColumnHeadersOptions.receivedColumnWidth,
        sortedContentColumnWidth: sortColumnHeaderType
            ? (sortedContentColumnWidthCache.get(sortColumnHeaderType) ??
                getColumnMinWidth(sortColumnHeaderType))
            : 0, // Zero as this is not a permanent column
    };
};

const getCurrentColumnWidths = (sortColumnHeaderType: MailListColumnHeaderType | null) => {
    const { senderColumnWidth, subjectColumnWidth, receivedColumnWidth, sortedContentColumnWidth } = getMailListLayoutStore();
    
    // If we have a sorted column, return widths as-is
    if (sortColumnHeaderType) {
        return {
            senderColumnWidth,
            subjectColumnWidth,
            receivedColumnWidth,
            sortedContentColumnWidth,
        };
    }
    
    // No sorted column - combine sorted width back into subject
    return {
        senderColumnWidth,
        subjectColumnWidth: sortedContentColumnWidth
            ? sortedContentColumnWidth + (subjectColumnWidth ?? 0)
            : subjectColumnWidth,
        receivedColumnWidth,
        sortedContentColumnWidth: 0,
    };
};

// This function stores the adjusted column widths (i.e. the user's preferred column
// widths + any adjustments made to fit the columns within the available space) in
// local storage so that we can render the columns at the appropriate width the
// next time the user reloads the page.
const updateLocalColumnWidths = (senderColumnWidth: number, subjectColumnWidth: number, receivedColumnWidth: number, sortedContentColumnWidth: number) => {
    setItem(window, `MailListColumnWidths`, JSON.stringify({
        senderColumnWidth,
        subjectColumnWidth,
        receivedColumnWidth,
        sortedContentColumnWidth,
    }));
};

/**
 * Updates the in-memory sorted column width cache with a user-specified value.
 * Call this when the user manually drags a resize handle to change the sorted
 * column width, so that the preference is preserved across window resizes and
 * folder navigation.
 */
export function updateSortedColumnWidthCache(sortColumnType: MailListColumnHeaderType, width: number): void {
    if (width > 0) {
        sortedContentColumnWidthCache.set(sortColumnType, width);
    }
}
